import {
  faAngleRight,
  faAngleLeft,
  faAnglesRight,
  faAnglesLeft,
  faDownload,
  faSearchPlus,
  faSearchMinus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Typography, Alert, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { READABLE_FILE_TYPES, READABLE_IMAGE_TYPES } from "./fileTypes";
import { State, DocxState, Props } from "./interfaces";
import OverlayingCanvas from "./OverlayingCanvas/OverlayingCanvas";
import {
  Container,
  Header,
  ThumbnailsContainer,
  MainViewerContainer,
  ToolsContainer,
  StyledFontAwesomeIcon,
  ThumbnailsAndMainViewerContainer,
} from "./styles";
import * as UTILS from "./utils/utils";
import LoadingOverlay from "../../containers/LoadingOverlay/LoadingOverlay";
import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";
import { DocumentLinkApiService } from "../../services/document-link/document-link-api.service";
import { handleDownloadDocFile } from "../../services/document-link/document-link-api.service";
import { DocumentFilesApiService } from "../../services/documents/document-files-api.service";
import { handleDownloadFile } from "../../services/documents/document-files-api.service";

import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { getFileExtension } from "../../utils/files-utils/get-file-extension";

const { DownloadDocuments } = AUTHORIZATIONS.LegalEntityAccess;
const { CompanyAccountsConfig } = AUTHORIZATIONS.SystemAccess;

const PDFJS = window.pdfjsLib;

//This is critical for the PDFJS module
PDFJS.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/build/pdf.worker.min.js"; //please make sure to always matching version with pdfjs-dist version

const DocumentViewer = ({
  fileDto,
  withFooter,
  documentLinkProps,
  bypasAllowDownload,
  file,
  customFileName,
  handleSuccessfullView,
  handleSuccessfullDownload,
  isGenericDocument,
  defaultMainScale,
  embedded,
  hideHeader = false,
  hidePagination = false,
  handleDrawingRectangleInCanvas,
  rectanglesToDrawIncanvas,
  handleClickRectangle,
  handleDeleteRectangle,
  hideThumbnails = false,
  allowNestedRectangles,
  customToolButtons,
  updateParentWithPageNumberChange,
  controlledPageNumber = 0,
  updateParentWithTotalPagesNumber,
}: Props) => {
  const allowDownload = useAuthorization([DownloadDocuments]) || bypasAllowDownload;
  const isSysAdmin = useAuthorization([CompanyAccountsConfig]);

  const mainCanvasRef = useRef<HTMLCanvasElement>(null);

  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.document_viewer";

  /** SPECIFS FOR DOCX ** */
  // #region docx state
  const [docxState, setDocxState] = useState<DocxState>({
    document_xml: "",
    document_xml_rel: "",
    media_files: [],
    allElements: [],
    docWidth: 0,
    docHeight: 0,
    docMarginTop: 0,
    docMarginRight: 0,
    docMarginBottom: 0,
    docMarginLeft: 0,
    headerHeight: 0,
    fotterHeight: 0,
    docRatio: 0,
    pages: [],
  });

  const docxStateRef = useRef<DocxState>(docxState);
  docxStateRef.current = docxState;
  // #endregion

  // #region state
  const [state, setState] = useState<State>({
    pagesNumber: 0,
    currentPageNumber: controlledPageNumber ?? 0,
    file: null,
    fileName: "",
    fileExtension: "",
    downloadUrl: "",
    mainScale: defaultMainScale ?? 1,
    imgSrc: "",
    loading: false,
    loadingPopulatePage: false,
    error: "",
  });
  // #endregion

  // // #region drawing rectangle state
  // const [drawingRectangleState, setDrawingRectangleState] = useState<DrawingRectangleState>({
  //   rectangles:
  //     rectanglesToDrawIncanvas?.filter((_rectangle) => _rectangle.pageNumber === state.currentPageNumber) ?? [],
  //   snapshot: null,
  // });
  // // #endregion

  // #region sheet_header_inline_style
  const sheet_header_inline_style = `<style>
                                            span {
                                                color : ${theme.palette.primary.contrastText} ;
                                                background-color : ${theme.palette.primary.main} ;
                                                padding:2px 5px;
                                                display: block;
                                                width: fit-content; }
                                        </style>`;
  // #endregion

  // #region handle set state
  const handleSetState = (fields: { [N: string]: any }) => {
    setState((state) => ({
      ...state,
      ...fields,
    }));
  };
  // #endregion

  // #region handle set docx state
  const handleSetDOCXState = (fields: { [N: string]: any }) => {
    setDocxState((state) => ({
      ...state,
      ...fields,
    }));
  };
  //#endregion

  //#region load file
  const loadFile = async (file_id: string) => {
    if (documentLinkProps) {
      const { name, data, error } = await new DocumentLinkApiService().loadBlob(
        documentLinkProps.documentLinkId,
        documentLinkProps.email,
        documentLinkProps.fourDigitsOfPhoneNumber,
        file_id
      );
      if (error === "") {
        toast.success(t(`${tRP}.toast_success_response`));
        const file_extension = getFileExtension(name);
        return { file_extension, name, data };
      } else toast.error(error, { autoClose: 10000 });
      return { error };
    } else {
      const loadPath = isGenericDocument ? "/GetGenericDocumentsFilesContents" : undefined;
      const { name, data, error } = await new DocumentFilesApiService().loadBlob(file_id, undefined, loadPath);

      // did not use the reusable toastify response because the response interface is different when using loadBlob ---> to review
      if (error === "") {
        toast.success(t(`${tRP}.toast_success_response`));
        const file_extension = getFileExtension(name);
        return { file_extension, name, data };
      } else if (error) toast.error(error, { autoClose: 10000 });
      return { error };
    }
  };
  //#endregion

  ///#region download file
  const _handleDonwloadFile = useCallback(async () => {
    if (file) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(file.file);
      link.download = file.fileName;
      link.click();
      handleSuccessfullDownload && handleSuccessfullDownload();
    }
    if (!documentLinkProps && !!fileDto) {
      handleDownloadFile(fileDto, customFileName, handleSuccessfullDownload, isGenericDocument);
    }
    if (documentLinkProps) {
      handleDownloadDocFile(
        documentLinkProps.documentLinkId,
        documentLinkProps.email,
        documentLinkProps.fourDigitsOfPhoneNumber,
        documentLinkProps.fileId
      );
    }
  }, [file, handleDownloadFile, documentLinkProps, fileDto, isGenericDocument]);
  //#endregion

  //#region init
  const init = useCallback(async (file_id?: string) => {
    try {
      const { file_extension, name, data, error } = file
        ? {
            file_extension: file.fileExtension,
            name: file.fileName,
            data: file.file,
            error: undefined,
          }
        : file_id
        ? await loadFile(file_id)
        : {
            file_extension: undefined,
            name: undefined,
            data: undefined,
            error: undefined,
          };
      if (error === undefined && file_extension !== undefined && name !== undefined && data !== undefined) {
        const fileExtension = file_extension.toLowerCase();

        if (READABLE_IMAGE_TYPES.includes(fileExtension)) {
          UTILS.processImages(decodeURI(name), data, handleSetState, fileExtension);
          handleSuccessfullView && handleSuccessfullView();
        } else if (READABLE_FILE_TYPES.includes(fileExtension)) {
          switch (fileExtension) {
            case "pdf":
              UTILS.processPDF(decodeURI(name), data, handleSetState, state.mainScale);
              handleSuccessfullView && handleSuccessfullView();
              // drawExistingRectangles();
              // drawingRectangles();

              break;
            case "xlsx":
              UTILS.processXLSX(decodeURI(name), data, handleSetState, state.mainScale, sheet_header_inline_style);
              handleSuccessfullView && handleSuccessfullView();
              break;
            case "xls":
              UTILS.processXLSX(decodeURI(name), data, handleSetState, state.mainScale, sheet_header_inline_style);
              handleSuccessfullView && handleSuccessfullView();
              break;
            case "docx":
              UTILS.parseDOCXMLFiles(
                decodeURI(name),
                data,
                handleSetState,
                docxState,
                docxStateRef,
                handleSetDOCXState
              );
              handleSuccessfullView && handleSuccessfullView();
              break;
            case "txt":
              UTILS.processTXTFiles(decodeURI(name), data, handleSetState);
              handleSuccessfullView && handleSuccessfullView();
              break;
            case "csv":
              UTILS.processCSVFiles(decodeURI(name), data, handleSetState);
              handleSuccessfullView && handleSuccessfullView();
              break;

            default:
              setState((state) => ({
                ...state,
                error: `${t(`${tRP}.unavailable_type_error_part_one`)} ${fileExtension} ${t(
                  `${tRP}.unavailable_type_error_part_two`
                )}`,
              }));
              break;
          }
        } else {
          setState((state) => ({
            ...state,
            error: `${t(`${tRP}.unavailable_type_error_part_one`)} ${fileExtension} ${t(
              `${tRP}.unavailable_type_error_part_two`
            )}`,
          }));
        }
      } else {
        setState((state) => ({ ...state, error: error as string }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, []);
  //#endregion

  useEffect(() => {
    if (state.fileExtension === "pdf") {
      (async () => {
        const page: typeof PDFJS.PDFPageProxy = await state.file.getPage(state.currentPageNumber + 1);
        // UTILS.populatePDFPages("main-viewer", page, state.mainScale);
        UTILS.populatePDFPages("main-viewer", page, 1);
        // drawExistingRectangles();
        // drawingRectangles();
        const all_thumbnails = document.querySelectorAll("canvas.thumbnail");
        all_thumbnails.forEach((thumbnail: Element) => {
          if (thumbnail.id === `page-${state.currentPageNumber}`) {
            thumbnail.classList.add("current");
            thumbnail.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
          } else thumbnail.classList.remove("current");
        });
      })();
    } else if (state.fileExtension === "xlsx") {
      state.file.SheetNames.forEach((sheet: any, idx: number) => {
        if (idx === state.currentPageNumber) {
          const html_data = XLSX.utils.sheet_to_html(state.file.Sheets[sheet]);

          UTILS.populateXLSPages(
            "main-viewer",
            { html_data, sheet_title: sheet },
            state.mainScale,
            sheet_header_inline_style
          );

          const all_thumbnails = document.querySelectorAll("#thumbnails_container div.thumbnail");

          all_thumbnails.forEach((thumbnail: Element) => {
            if (thumbnail.id === `page-${state.currentPageNumber}`) {
              thumbnail.classList.add("current");
              thumbnail.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
            } else thumbnail.classList.remove("current");
          });
        }
      });
    } else if (state.fileExtension === "docx") {
      const page = docxState.pages.filter((page) => {
        return page.pageNumber === state.currentPageNumber;
      });

      if (page[0] !== undefined) {
        for (let i = 0; i < page[0].elements.length; i++) {
          UTILS.populateDOCXPages(false, state.currentPageNumber, page[0].elements, true, docxStateRef, handleSetState);
        }
      }
    }

    updateParentWithPageNumberChange && updateParentWithPageNumberChange(state.currentPageNumber);
  }, [state.currentPageNumber, updateParentWithPageNumberChange]);

  useEffect(() => {
    if (state.file !== null && state.fileExtension === "pdf") {
      (async () => {
        const page: typeof PDFJS.PDFPageProxy = await state.file.getPage(state.currentPageNumber);

        const canvas = document.getElementById("main-viewer") as HTMLCanvasElement;
        if (canvas !== null) {
          canvas.height = canvas.height * state.mainScale;
          canvas.width = canvas.width * state.mainScale;
        }

        UTILS.populatePDFPages("main-viewer", page, state.mainScale);
        // drawExistingRectangles();
        // drawingRectangles();
      })();
    }

    if (state.fileExtension === "docx") {
      const main_viewer = document.getElementById("main-viewer");
      if (main_viewer !== null) main_viewer.style.scale = `${state.mainScale}`;
    }

    if (
      state.fileExtension === "png" ||
      state.fileExtension === "jpg" ||
      state.fileExtension === "jpeg" ||
      state.fileExtension === "gif" ||
      state.fileExtension === "tif" ||
      state.fileExtension === "svg"
    ) {
      const img_container = document.getElementById("img");
      if (img_container !== null) img_container.style.scale = `${state.mainScale}`;
    }
  }, [state.mainScale, state.file]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      loading: true,
    }));
    const file_id: string | undefined = fileDto?.id ?? documentLinkProps?.fileId;

    if (file_id) {
      init(file_id);
    } else if (file) {
      init();
    } else {
      setState((state) => ({
        ...state,
        pagesNumber: 0,
        currentPageNumber: controlledPageNumber ?? 0,
        file: null,
        fileName: "",
        fileExtension: "",
        downloadUrl: "",
        mainScale: 1,
        imgSrc: "",
        loading: false,
        loadingPopulatePage: false,
        error: "No file was provided",
      }));
    }
  }, [fileDto?.id, documentLinkProps?.fileId]);

  // #region render custom tools container
  const renderCustomToolsContainer = () => {
    if (customToolButtons) {
      const Tools = (
        <ToolsContainer style={{ width: "100%" }}>
          {customToolButtons.firstPageButton &&
            customToolButtons.firstPageButton({
              onClick: () => {
                setState({
                  ...state,
                  currentPageNumber: 0,
                });
              },
              disabled: state.currentPageNumber === 0,
            })}

          {customToolButtons.previousPageButton &&
            customToolButtons.previousPageButton({
              onClick: () => {
                if (state.currentPageNumber > 0) {
                  setState({
                    ...state,
                    currentPageNumber: state.currentPageNumber - 1,
                  });
                }
              },
              style: {
                color: state.currentPageNumber === 0 ? theme.palette.secondary.light : theme.palette.primary.main,
                borderColor: state.currentPageNumber === 0 ? theme.palette.secondary.light : theme.palette.primary.main,
              },
            })}

          {customToolButtons.currentPageNumberDisplayer &&
            customToolButtons.currentPageNumberDisplayer(state.currentPageNumber, state.pagesNumber)}

          {customToolButtons.nextPageButton &&
            customToolButtons.nextPageButton({
              onClick: () => {
                if (
                  state.currentPageNumber < state.pagesNumber &&
                  state.currentPageNumber + 1 <= state.pagesNumber - 1
                ) {
                  setState({
                    ...state,
                    currentPageNumber: state.currentPageNumber + 1,
                  });
                }
              },
              disabled: state.currentPageNumber === state.pagesNumber - 1,
              style: {
                color:
                  state.currentPageNumber === state.pagesNumber - 1
                    ? theme.palette.secondary.light
                    : theme.palette.primary.main,
                borderColor:
                  state.currentPageNumber === state.pagesNumber - 1
                    ? theme.palette.secondary.light
                    : theme.palette.primary.main,
              },
            })}

          {customToolButtons.lastPageButton &&
            customToolButtons.lastPageButton({
              onClick: () => {
                setState({
                  ...state,
                  currentPageNumber: state.pagesNumber - 1,
                });
              },
            })}
        </ToolsContainer>
      );

      const toolsPortalDomNode = document.getElementById(customToolButtons.domNodeId ?? "");

      if (toolsPortalDomNode) {
        return createPortal(Tools, toolsPortalDomNode);
      } else {
        return Tools;
      }
    }

    return null;
  };
  // #endregion

  // #region update with controlled page number
  useEffect(() => {
    setState((previousState) => ({
      ...previousState,
      currentPageNumber: controlledPageNumber,
    }));
  }, [controlledPageNumber]);
  // #endregion

  useEffect(() => {
    updateParentWithTotalPagesNumber && updateParentWithTotalPagesNumber(state.pagesNumber);
  }, [state.pagesNumber]);

  return (
    <LoadingOverlay loading={state.loading}>
      <Container
        embedded={embedded}
        container
        className={withFooter ? "withFooter" : ""}
        id="document-viewer-container"
      >
        {!hideHeader && (
          <Header
            item
            xs={12}
          >
            <Typography variant="h5"> {state.fileName.split(".")[0]} </Typography>

            <div>
              {(allowDownload || isSysAdmin || !!documentLinkProps) && (
                <IconButton
                  onClick={_handleDonwloadFile}
                  title={t(`${tRP}.download`)}
                  style={{
                    fontSize: "2rem",
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </IconButton>
              )}
            </div>
          </Header>
        )}
        {customToolButtons && customToolButtons.atTheTop && renderCustomToolsContainer()}
        <ThumbnailsAndMainViewerContainer>
          {!hideThumbnails && (
            <ThumbnailsContainer
              item
              xs={1.5}
              id="thumbnails_container"
            >
              {(state.fileExtension === "txt" || state.fileExtension === "csv") && (
                <div
                  className="thumbnail"
                  id="page-0"
                ></div>
              )}

              {state.pagesNumber > 0 &&
                [...Array(state.pagesNumber).keys()].map((page) => {
                  return (
                    <React.Fragment key={page}>
                      {state.fileExtension === "pdf" && (
                        <canvas
                          key={page}
                          className="thumbnail"
                          id={`page-${page}`}
                          onClick={() => {
                            setState({
                              ...state,
                              currentPageNumber: page,
                            });
                          }}
                        />
                      )}

                      {(state.fileExtension === "xlsx" ||
                        state.fileExtension === "xls" ||
                        state.fileExtension === "docx") && (
                        <div
                          key={page}
                          className="thumbnail"
                          id={`page-${page}`}
                          onClick={() => {
                            setState({
                              ...state,
                              currentPageNumber: page,
                            });
                          }}
                        ></div>
                      )}
                    </React.Fragment>
                  );
                })}
            </ThumbnailsContainer>
          )}

          <MainViewerContainer
            className="main-viewer-container"
            // item
            // xs={10.5}
            id={`page-${state.currentPageNumber}`}
          >
            <Grid>
              {!state.error && READABLE_FILE_TYPES.includes(state.fileExtension) ? (
                state.fileExtension === "pdf" ? (
                  <>
                    <canvas
                      id="main-viewer"
                      ref={mainCanvasRef}
                    />

                    <OverlayingCanvas
                      pageNumber={state.currentPageNumber}
                      rectangles={rectanglesToDrawIncanvas ?? []}
                      handleDeleteRectangle={handleDeleteRectangle}
                      handleDrawnRectangle={handleDrawingRectangleInCanvas}
                      handleClickRectangle={handleClickRectangle}
                      allowNestedRectangles={allowNestedRectangles}
                    />

                    <LoadingOverlay loading={state.loadingPopulatePage}></LoadingOverlay>
                  </>
                ) : (
                  <>
                    <div id="main-viewer"></div>
                    <LoadingOverlay loading={state.loadingPopulatePage}></LoadingOverlay>
                  </>
                )
              ) : READABLE_IMAGE_TYPES.includes(state.fileExtension) ? (
                <>
                  <div id="main-viewer">
                    <div id="imgContainer">
                      <img
                        src={state.imgSrc}
                        alt={state.fileName}
                        id="img"
                      />
                    </div>
                  </div>
                  <LoadingOverlay loading={state.loadingPopulatePage}></LoadingOverlay>
                </>
              ) : (
                <></>
              )}
              {state.error && <Alert severity="error">{state.error}</Alert>}
            </Grid>
          </MainViewerContainer>
        </ThumbnailsAndMainViewerContainer>
        {customToolButtons ? (
          customToolButtons.atTheTop ? (
            <></>
          ) : (
            renderCustomToolsContainer()
          )
        ) : (
          <>
            {hidePagination ? (
              <></>
            ) : (
              <ToolsContainer
                item
                xs={10.5}
              >
                {state.fileExtension && !READABLE_IMAGE_TYPES.includes(state.fileExtension) && (
                  <Typography align="center">{`page ${state.currentPageNumber + 1} / ${state.pagesNumber}`}</Typography>
                )}
                <IconButton
                  title={t(`${tRP}.zoom_out`)}
                  onClick={() => {
                    if (state.mainScale >= 0.4) {
                      setState({
                        ...state,
                        mainScale: state.mainScale - 0.2,
                      });
                    }
                  }}
                  disabled={state.fileExtension === "xlsx"}
                >
                  <StyledFontAwesomeIcon icon={faSearchMinus} />
                </IconButton>

                <IconButton
                  title={t(`${tRP}.zoom_in`)}
                  onClick={() => {
                    if (state.mainScale <= 3) {
                      setState({
                        ...state,
                        mainScale: state.mainScale + 0.2,
                      });
                    }
                  }}
                  disabled={state.fileExtension === "xlsx"}
                >
                  <StyledFontAwesomeIcon icon={faSearchPlus} />
                </IconButton>

                {!READABLE_IMAGE_TYPES.includes(state.fileExtension) && (
                  <>
                    <IconButton
                      title={t(`${tRP}.first_page`)}
                      onClick={() => {
                        setState({
                          ...state,
                          currentPageNumber: 0,
                        });
                      }}
                    >
                      <StyledFontAwesomeIcon icon={faAnglesLeft} />
                    </IconButton>

                    <IconButton
                      title={t(`${tRP}.previous_page`)}
                      onClick={() => {
                        if (state.currentPageNumber > 0) {
                          setState({
                            ...state,
                            currentPageNumber: state.currentPageNumber - 1,
                          });
                        }
                      }}
                    >
                      <StyledFontAwesomeIcon icon={faAngleLeft} />
                    </IconButton>

                    <IconButton
                      title={t(`${tRP}.next_page`)}
                      onClick={() => {
                        if (
                          state.currentPageNumber < state.pagesNumber &&
                          state.currentPageNumber + 1 <= state.pagesNumber - 1
                        ) {
                          setState({
                            ...state,
                            currentPageNumber: state.currentPageNumber + 1,
                          });
                        }
                      }}
                    >
                      <StyledFontAwesomeIcon icon={faAngleRight} />
                    </IconButton>

                    <IconButton
                      title={t(`${tRP}.last_page`)}
                      onClick={() => {
                        setState({
                          ...state,
                          currentPageNumber: state.pagesNumber - 1,
                        });
                      }}
                    >
                      <StyledFontAwesomeIcon icon={faAnglesRight} />
                    </IconButton>
                  </>
                )}
              </ToolsContainer>
            )}
          </>
        )}
      </Container>
    </LoadingOverlay>
  );
};

export default memo(DocumentViewer);
